import moment from 'moment';
import { isMediaAggregate, mediaByDay } from '../mediaAggregates';
import { t } from '../i18n';

export const MOVES_ROUTE_TYPES = ['walking', 'running', 'cycling', 'transport', 'car'];
export const ROUTE_EVENT_TYPES = ['health', 'google_maps', ...MOVES_ROUTE_TYPES];

const createRouteEvents = (eventType) => ({ routeItems }) =>
  (routeItems || [])
    .filter(routeItem => routeItem.eventType === eventType)
    .filter(routeItem => routeItem.date)
    .map(routeItem => {
      const startTime = moment(routeItem.date);
      const firstPoint = routeItem.route[0] || {};
      const endTime = routeItem.sourceKey === 'health' && firstPoint.duration
        ? moment(startTime).add(firstPoint.duration, 'seconds')
        : moment(startTime).add(1, 'hour');
      const title = routeItem.sourceKey === 'moves'
        ? `${routeItem.label} (${routeItem.typeLabel})`
        : routeItem.label;

      return {
        title,
        start: startTime.toDate(),
        end: endTime.toDate(),
        allDay: routeItem.sourceKey !== 'health' || !firstPoint.duration,
        resource: {
          type: routeItem.eventType,
          data: routeItem.route,
          routeId: routeItem.id,
          color: routeItem.color,
        },
      };
    });

const createTraktMovieEvents = ({ data }) => {
  if (isMediaAggregate(data.movies)) {
    return (mediaByDay(data.movies) || [])
      .filter((row) => row.date && row.count > 0)
      .map((row) => ({
        title: t('web.registry.moviesCount', { count: row.count }),
        start: moment(row.date).toDate(),
        end: moment(row.date).toDate(),
        allDay: true,
        resource: {
          type: 'movie',
          data: { date: row.date, count: row.count, _aggregateDay: true },
          color: '#e74c3c',
        },
      }));
  }
  return (data.movies || [])
    .filter(item => item.item_type === 'movie' && item.watched_at)
    .map(item => ({
      title: t('web.registry.movieTitle', { title: item.title, year: item.year }),
      start: moment(item.watched_at).toDate(),
      end: moment(item.watched_at).toDate(),
      allDay: true,
      resource: { type: 'movie', data: item, color: '#e74c3c' },
    }));
};

const createTraktShowEvents = ({ data }) => {
  if (isMediaAggregate(data.shows)) {
    return (mediaByDay(data.shows) || [])
      .filter((row) => row.date && row.count > 0)
      .map((row) => ({
        title: t('web.registry.showsEps', { count: row.count }),
        start: moment(row.date).toDate(),
        end: moment(row.date).toDate(),
        allDay: true,
        resource: {
          type: 'shows',
          data: { date: row.date, count: row.count, _aggregateDay: true },
          color: '#9b59b6',
        },
      }));
  }
  return (data.shows || [])
    .filter(item => item.item_type === 'episode' && item.watched_at)
    .map(item => ({
      title: t('web.registry.showTitle', { show: item.show_title, season: String(item.season_number).padStart(2, '0'), episode: String(item.episode_number).padStart(2, '0'), title: item.episode_title || item.title }),
      start: moment(item.watched_at).toDate(),
      end: moment(item.watched_at).toDate(),
      allDay: true,
      resource: { type: 'shows', data: item, color: '#9b59b6' },
    }));
};

// Agrega as músicas por dia: um único evento "N músicas" por dia, em vez de
// um evento por faixa (que explodia o calendário com dezenas de milhares de
// itens). O resource carrega todas as faixas do dia para o card detalhado
// (ou plays:[] + fetch /api/music quando mediaAggregates).
const createMusicEvents = (dataKey, eventType, color, serviceLabel) => ({ data }) => {
  const source = data[dataKey];
  if (isMediaAggregate(source)) {
    return (mediaByDay(source) || [])
      .filter((row) => row.date && row.count > 0)
      .map((row) => ({
        title: `${serviceLabel}: ${row.count} ${row.count === 1 ? t('web.registry.musicOne') : t('web.registry.musicMany')}`,
        start: moment(row.date).toDate(),
        end: moment(row.date).toDate(),
        allDay: true,
        resource: {
          type: eventType,
          data: {
            date: row.date,
            service: serviceLabel,
            count: row.count,
            plays: [],
            _needsFetch: true,
          },
          color,
        },
      }));
  }

  const plays = (source || []).filter(item => item.played_at && item.track_name);

  const byDay = new Map();
  plays.forEach(item => {
    const day = String(item.played_at).slice(0, 10); // YYYY-MM-DD
    if (!byDay.has(day)) byDay.set(day, []);
    byDay.get(day).push(item);
  });

  return [...byDay.entries()].map(([day, dayPlays]) => {
    const sorted = [...dayPlays].sort((a, b) =>
      String(a.played_at).localeCompare(String(b.played_at))
    );
    return {
      title: `${serviceLabel}: ${dayPlays.length} ${dayPlays.length === 1 ? t('web.registry.musicOne') : t('web.registry.musicMany')}`,
      start: moment(day).toDate(),
      end: moment(day).toDate(),
      allDay: true,
      resource: {
        type: eventType,
        data: { date: day, service: serviceLabel, count: dayPlays.length, plays: sorted },
        color,
      },
    };
  });
};

export const FEATURE_REGISTRY = [
  {
    key: 'checkin',
    filterKey: 'checkin',
    label: t('web.registry.label.checkins'),
    group: t('web.registry.group.places'),
    dataKey: 'checkins',
    calendar: {
      eventType: 'checkin',
      color: '#3498db',
      createEvents: ({ data }) =>
        (data.checkins || [])
          .filter(item => item.date)
          .map(item => ({
            title: t('web.registry.checkinTitle', { venue: item.venue || t('web.registry.unknownPlace') }),
            start: moment(item.date).toDate(),
            end: moment(item.date).toDate(),
            allDay: true,
            resource: { type: 'checkin', data: item, color: '#3498db' },
          })),
    },
    mapAction: { type: 'flyToMarker', coordinateFields: ['lat', 'lon'] },
    importAction: {
      kind: 'sync',
      endpoint: 'foursquare',
      serviceLabel: 'Foursquare',
      title: 'Foursquare / Swarm',
      description: t('web.registry.desc.foursquare'),
      buttonLabel: t('web.registry.syncCheckins'),
      buttonClassName: 'foursquare-btn',
    },
  },
  {
    key: 'walking',
    filterKey: 'walking',
    label: t('web.registry.walk'),
    group: t('web.registry.group.movesRoutes'),
    dataKey: 'moves',
    routeSource: 'moves',
    routeType: 'walking',
    calendar: { eventType: 'walking', createEvents: createRouteEvents('walking') },
    mapAction: { type: 'focusRoute' },
  },
  {
    key: 'running',
    filterKey: 'running',
    label: t('web.registry.run'),
    group: t('web.registry.group.movesRoutes'),
    dataKey: 'moves',
    routeSource: 'moves',
    routeType: 'running',
    calendar: { eventType: 'running', createEvents: createRouteEvents('running') },
    mapAction: { type: 'focusRoute' },
  },
  {
    key: 'cycling',
    filterKey: 'cycling',
    label: t('web.registry.cycling'),
    group: t('web.registry.group.movesRoutes'),
    dataKey: 'moves',
    routeSource: 'moves',
    routeType: 'cycling',
    calendar: { eventType: 'cycling', createEvents: createRouteEvents('cycling') },
    mapAction: { type: 'focusRoute' },
  },
  {
    key: 'transport',
    filterKey: 'transport',
    label: t('web.registry.transit'),
    group: t('web.registry.group.movesRoutes'),
    dataKey: 'moves',
    routeSource: 'moves',
    routeType: 'transport',
    calendar: { eventType: 'transport', createEvents: createRouteEvents('transport') },
    mapAction: { type: 'focusRoute' },
  },
  {
    key: 'car',
    filterKey: 'car',
    label: t('web.registry.car'),
    group: t('web.registry.group.movesRoutes'),
    dataKey: 'moves',
    routeSource: 'moves',
    routeType: 'car',
    calendar: { eventType: 'car', createEvents: createRouteEvents('car') },
    mapAction: { type: 'focusRoute' },
  },
  {
    key: 'health',
    filterKey: 'health',
    label: t('web.registry.workouts'),
    group: t('web.registry.group.health'),
    dataKey: 'health',
    routeSource: 'health',
    calendar: { eventType: 'health', createEvents: createRouteEvents('health') },
    mapAction: { type: 'focusRoute' },
    importAction: {
      kind: 'upload',
      endpoint: '/api/upload/health',
      uploadKey: 'health',
      fileField: 'files',
      accept: '.gpx',
      multiple: true,
      title: 'Apple Health (GPX)',
      description: t('web.registry.desc.health'),
      emptyMessage: t('web.registry.empty.health'),
      uploadingMessage: count => t('web.registry.uploading.health', { count }),
      buttonLabel: t('web.registry.importGpx'),
      buttonClassName: 'health-btn',
    },
  },
  {
    key: 'google_maps',
    filterKey: 'google_maps',
    label: t('web.registry.label.googleMaps'),
    group: t('web.registry.group.googleMaps'),
    dataKey: 'googleMaps',
    routeSource: 'google_maps',
    calendar: { eventType: 'google_maps', createEvents: createRouteEvents('google_maps') },
    mapAction: { type: 'focusRoute' },
    importAction: {
      kind: 'upload',
      endpoint: '/api/upload/google-maps',
      uploadKey: 'googleMaps',
      fileField: 'files',
      accept: '.json,.zip,application/json,application/zip',
      multiple: true,
      title: 'Google Maps Timeline',
      description: t('web.registry.desc.googleMaps'),
      emptyMessage: t('web.registry.empty.googleMaps'),
      uploadingMessage: count => t('web.registry.uploading.googleMaps', { count }),
      buttonLabel: t('web.registry.importTimeline'),
      buttonClassName: 'google-maps-btn',
    },
  },
  {
    key: 'movie',
    filterKey: 'movie',
    label: t('web.registry.label.movies'),
    group: t('web.registry.group.entertainment'),
    dataKey: 'movies',
    calendar: { eventType: 'movie', color: '#e74c3c', createEvents: createTraktMovieEvents },
    importAction: {
      kind: 'sync',
      endpoint: 'trakt',
      serviceLabel: 'Trakt',
      title: 'Trakt.tv',
      description: t('web.registry.desc.trakt'),
      buttonLabel: t('web.registry.syncTrakt'),
      buttonClassName: 'trakt-btn',
      sharedWith: ['shows'],
    },
  },
  {
    key: 'shows',
    filterKey: 'shows',
    label: t('web.registry.label.shows'),
    group: t('web.registry.group.entertainment'),
    dataKey: 'shows',
    calendar: { eventType: 'shows', color: '#9b59b6', createEvents: createTraktShowEvents },
    importAction: {
      kind: 'sync',
      endpoint: 'trakt',
      serviceLabel: 'Trakt',
      renderedBy: 'movie',
    },
  },
  {
    key: 'concerts',
    filterKey: 'concerts',
    label: t('web.registry.label.concerts'),
    group: t('web.registry.group.entertainment'),
    dataKey: 'concerts',
    calendar: {
      eventType: 'concerts',
      color: '#1abc9c',
      createEvents: ({ data }) =>
        (data.concerts || [])
          .filter(item => item.event_date)
          .map(item => {
            const venue = item.venue_name ? ` @ ${item.venue_name}` : '';
            const city = item.city_name ? `, ${item.city_name}` : '';
            return {
              title: t('web.registry.concertTitle', { artist: item.artist_name || t('web.registry.unknownArtist'), venue, city }),
              start: moment(item.event_date).toDate(),
              end: moment(item.event_date).toDate(),
              allDay: true,
              resource: { type: 'concerts', data: item, color: '#1abc9c' },
            };
          }),
    },
    card: { type: 'setlistfm' },
    mapAction: { type: 'openSetlistCardAndFlyToMarker', coordinateFields: ['latitude', 'longitude'] },
    importAction: {
      kind: 'sync',
      endpoint: 'setlistfm',
      serviceLabel: 'setlist.fm',
      title: 'setlist.fm',
      description: t('web.registry.desc.setlistfm'),
      buttonLabel: t('web.registry.syncShows'),
      buttonClassName: 'setlistfm-btn',
    },
  },
  {
    key: 'spotify',
    filterKey: 'spotify',
    label: 'Spotify',
    group: t('web.registry.group.music'),
    dataKey: 'spotify',
    calendar: {
      eventType: 'spotify',
      color: '#1db954',
      createEvents: createMusicEvents('spotify', 'spotify', '#1db954', 'Spotify'),
    },
    card: { type: 'music' },
    importAction: {
      kind: 'upload',
      endpoint: '/api/upload/spotify',
      uploadKey: 'spotify',
      fileField: 'files',
      accept: '.json,.zip,application/json,application/zip',
      multiple: true,
      title: 'Spotify',
      description: t('web.registry.desc.spotify'),
      emptyMessage: t('web.registry.empty.spotify'),
      uploadingMessage: count => t('web.registry.uploading.spotify', { count }),
      buttonLabel: t('web.registry.importSpotify'),
      buttonClassName: 'spotify-btn',
    },
  },
  {
    key: 'apple_music',
    filterKey: 'apple_music',
    label: 'Apple Music',
    group: t('web.registry.group.music'),
    dataKey: 'appleMusic',
    calendar: {
      eventType: 'apple_music',
      color: '#fc3c44',
      createEvents: createMusicEvents('appleMusic', 'apple_music', '#fc3c44', 'Apple Music'),
    },
    card: { type: 'music' },
    importAction: {
      kind: 'upload',
      endpoint: '/api/upload/apple-music',
      uploadKey: 'appleMusic',
      fileField: 'files',
      accept: '.csv,.json,.zip,text/csv,application/json,application/zip',
      multiple: true,
      title: 'Apple Music',
      description: t('web.registry.desc.appleMusic'),
      emptyMessage: t('web.registry.empty.appleMusic'),
      uploadingMessage: count => t('web.registry.uploading.appleMusic', { count }),
      buttonLabel: t('web.registry.importAppleMusic'),
      buttonClassName: 'apple-music-btn',
    },
  },
  {
    key: 'diary',
    filterKey: 'diary',
    label: t('web.registry.diary'),
    group: t('web.registry.group.personal'),
    dataKey: 'diary',
    calendar: {
      eventType: 'diary',
      createEvents: ({ data, getMoodColor }) =>
        (data.diary || [])
          .filter(item => item.entry_date)
          .map(item => {
            const moodLabel = item.mood ? `${item.mood} ` : '';
            const title = item.note_title
              ? `${moodLabel}📓 ${item.note_title}`
              : `${moodLabel}📓 ${t('web.registry.diaryEvent')}`;
            return {
              title,
              start: moment(item.entry_date).toDate(),
              end: moment(item.entry_date).toDate(),
              allDay: true,
              resource: { type: 'diary', data: item, color: getMoodColor(item.mood) },
            };
          }),
    },
    card: { type: 'diary' },
    importAction: {
      kind: 'upload',
      endpoint: '/api/upload/diary',
      uploadKey: 'diary',
      fileField: 'file',
      accept: '.csv',
      multiple: false,
      title: t('web.registry.title.diaryCsv'),
      description: t('web.registry.desc.diary'),
      emptyMessage: t('web.registry.empty.diary'),
      uploadingMessage: () => t('web.registry.uploading.diary'),
      buttonLabel: t('web.registry.importCsv'),
      buttonClassName: 'diary-btn',
    },
  },
  {
    key: 'photos',
    filterKey: 'photos',
    label: t('web.registry.label.photos'),
    group: t('web.registry.group.personal'),
    dataKey: 'photos',
    calendar: {
      eventType: 'photos',
      color: '#f39c12',
      createEvents: ({ data }) =>
        (data.photos || [])
          .filter(item => item.photo_date)
          .map(item => ({
            title: item.title ? `📷 ${item.title}` : `📷 ${t('web.registry.photoEvent')}`,
            start: moment(item.photo_date).toDate(),
            end: moment(item.photo_date).toDate(),
            allDay: true,
            resource: { type: 'photos', data: item, color: '#f39c12' },
          })),
    },
    mapAction: { type: 'flyToMarker', coordinateFields: ['latitude', 'longitude'] },
    importAction: {
      kind: 'upload',
      endpoint: '/api/upload/photos',
      uploadKey: 'photos',
      fileField: 'file',
      accept: '.csv',
      multiple: false,
      title: t('web.registry.title.photosCsv'),
      description: t('web.registry.desc.photos'),
      emptyMessage: t('web.registry.empty.photos'),
      uploadingMessage: () => t('web.registry.uploading.photos'),
      buttonLabel: t('web.registry.importCsv'),
      buttonClassName: 'photos-btn',
    },
  },
  {
    key: 'memory_book',
    label: t('web.registry.label.memoryBook'),
    group: t('web.registry.group.reports'),
    dataKey: 'memoryBook',
    card: { type: 'memory_book' },
    reportAction: {
      kind: 'modal',
      title: t('web.registry.label.memoryBook'),
      buttonLabel: `📘 ${t('web.registry.label.memoryBook')}`,
      description: t('web.registry.desc.memoryBook'),
      buttonClassName: 'memory-book-btn',
    },
  },
  {
    key: 'manual',
    label: t('web.registry.label.manual'),
    group: t('web.registry.group.personal'),
    importAction: {
      kind: 'manual',
      title: t('web.registry.label.manual'),
      description: t('web.registry.desc.manual'),
      buttonLabel: t('web.registry.manualEntry'),
      buttonClassName: 'manual-btn',
    },
  },
];

export const FEATURE_BY_KEY = FEATURE_REGISTRY.reduce((acc, feature) => {
  acc[feature.key] = feature;
  return acc;
}, {});

export const EVENT_GROUPS = FEATURE_REGISTRY.reduce((groups, feature) => {
  if (!feature.filterKey) return groups;
  let group = groups.find(item => item.group === feature.group);
  if (!group) {
    group = { group: feature.group, types: [] };
    groups.push(group);
  }

  group.types.push({ key: feature.filterKey, label: feature.label });
  return groups;
}, []);

export const EVENT_TYPES = FEATURE_REGISTRY
  .filter(feature => feature.filterKey)
  .map(feature => ({
  key: feature.filterKey,
  label: feature.label,
}));

export const IMPORT_ACTIONS = FEATURE_REGISTRY
  .filter(feature => feature.importAction && !feature.importAction.renderedBy)
  .filter(feature => !feature.importAction.sharedWith || feature.key === 'movie')
  .map(feature => ({
    featureKey: feature.key,
    ...feature.importAction,
  }));

export const REPORT_ACTIONS = FEATURE_REGISTRY
  .filter(feature => feature.reportAction)
  .map(feature => ({
    featureKey: feature.key,
    ...feature.reportAction,
  }));

export const isRouteEventType = (type) => ROUTE_EVENT_TYPES.includes(type);

export const getFeatureKeys = () => FEATURE_REGISTRY.map(feature => feature.key);

export const getFeatureKeysByCapability = (capability) => {
  if (capability === 'filter') return FEATURE_REGISTRY.filter(feature => feature.filterKey).map(feature => feature.filterKey);
  if (capability === 'calendar') return FEATURE_REGISTRY.filter(feature => feature.calendar).map(feature => feature.key);
  if (capability === 'card') return FEATURE_REGISTRY.filter(feature => feature.card).map(feature => feature.key);
  if (capability === 'import') return FEATURE_REGISTRY.filter(feature => feature.importAction).map(feature => feature.key);
  if (capability === 'report') return FEATURE_REGISTRY.filter(feature => feature.reportAction).map(feature => feature.key);
  if (capability === 'map') return FEATURE_REGISTRY.filter(feature => feature.mapAction).map(feature => feature.key);
  return [];
};

export const createCalendarEventsFromRegistry = ({ data, selectedTypes, routeItems, getMoodColor, focusedKey }) => {
  const selected = new Set(selectedTypes || []);

  return FEATURE_REGISTRY.flatMap(feature => {
    if (!feature.calendar || !selected.has(feature.filterKey)) return [];

    const events = feature.calendar.createEvents({
      data,
      routeItems,
      feature,
      getMoodColor,
    });

    if (focusedKey) {
      const isFocused = focusedKey === feature.filterKey;
      if (!isFocused) {
        return events.map(event => ({
          ...event,
          resource: { ...event.resource, muted: true },
        }));
      }
    }

    return events;
  });
};

// Modos prontos de navegação: cada preset liga um subconjunto de filterKeys
// já existentes no registry. "Código" (GitHub) fica de fora por enquanto
// porque ainda não é uma feature registrada com filterKey (é exibido à parte
// via modal de contribuições), então incluí-lo violaria a regra de nunca
// apontar para um filterKey inexistente.
export const NAVIGATION_PRESETS = [
  {
    key: 'all',
    label: t('web.registry.lens.all'),
    icon: '🌐',
    filterKeys: FEATURE_REGISTRY.filter(feature => feature.filterKey).map(feature => feature.filterKey),
  },
  {
    key: 'trips',
    label: t('web.registry.lens.trips'),
    icon: '🧳',
    filterKeys: ['checkin', 'walking', 'running', 'cycling', 'transport', 'car', 'health', 'google_maps'],
  },
  {
    key: 'routes',
    label: t('web.registry.lens.routes'),
    icon: '🚶',
    filterKeys: ['walking', 'running', 'cycling', 'transport', 'car', 'health', 'google_maps'],
  },
  {
    key: 'music',
    label: t('web.registry.lens.music'),
    icon: '🎧',
    filterKeys: ['spotify', 'apple_music'],
  },
  {
    key: 'diary',
    label: t('web.registry.diary'),
    icon: '📓',
    filterKeys: ['diary'],
  },
  {
    key: 'entertainment',
    label: t('web.registry.lens.entertainment'),
    icon: '🎬',
    filterKeys: ['movie', 'shows', 'concerts'],
  },
];

export const NAVIGATION_PRESET_BY_KEY = NAVIGATION_PRESETS.reduce((acc, preset) => {
  acc[preset.key] = preset;
  return acc;
}, {});

// Função pura: recebe o preset clicado, os filtros atuais e a chave do preset
// ativo, e devolve o próximo estado. Clicar num preset já ativo desliga
// (limpa a seleção); clicar em outro preset liga exatamente os filterKeys
// dele, substituindo a seleção anterior.
export const applyNavigationPreset = (preset, currentFilters, activePresetKey) => {
  const baseFilters = currentFilters || {};

  if (!preset) {
    return { filters: baseFilters, activePresetKey: activePresetKey || null };
  }

  if (activePresetKey === preset.key) {
    return {
      filters: { ...baseFilters, selectedTypes: [] },
      activePresetKey: null,
    };
  }

  return {
    filters: { ...baseFilters, selectedTypes: [...(preset.filterKeys || [])] },
    activePresetKey: preset.key,
  };
};

// Detecta qual preset (se algum) corresponde exatamente à seleção atual, para
// que a UI possa destacar o botão certo mesmo após navegação por outros meios
// (ex: chips do TopBar, busca global).
export const detectActiveNavigationPreset = (selectedTypes) => {
  const selectedSet = new Set(selectedTypes || []);
  if (selectedSet.size === 0) return null;

  const match = NAVIGATION_PRESETS.find(preset => (
    preset.filterKeys.length === selectedSet.size
    && preset.filterKeys.every(key => selectedSet.has(key))
  ));

  return match ? match.key : null;
};

// ─── Contrato único de filtro ──────────────────────────────────────────
// Toda mudança de filtro passa por aqui, vinda de chip, checkbox, preset
// ou isolamento da legenda. O modelo inclui `focusedKey` para que o
// isolamento da legenda seja parte do mesmo estado (em vez de viver
// separado em `isolatedLayerKey`).

export const createEmptyFilterState = () => ({
  selectedTypes: EVENT_TYPES.map(et => et.key),
  startDate: null,
  endDate: null,
  focusedKey: null,
});

export const FILTER_CHANGE_KINDS = {
  TOGGLE_TYPE: 'TOGGLE_TYPE',
  SET_TYPES: 'SET_TYPES',
  CLEAR_TYPES: 'CLEAR_TYPES',
  SET_DATE_RANGE: 'SET_DATE_RANGE',
  SET_FOCUSED_KEY: 'SET_FOCUSED_KEY',
  CLEAR_FOCUSED_KEY: 'CLEAR_FOCUSED_KEY',
};

export const applyFilterChange = (currentFilters, change) => {
  const filters = currentFilters || {};

  switch (change.kind) {
    case FILTER_CHANGE_KINDS.TOGGLE_TYPE: {
      const key = change.filterKey;
      if (!key) return filters;

      const next = filters.selectedTypes ? [...filters.selectedTypes] : [...EVENT_TYPES.map(et => et.key)];
      const idx = next.indexOf(key);
      if (idx === -1) {
        next.push(key);
      } else {
        next.splice(idx, 1);
      }
      return { ...filters, selectedTypes: next };
    }

    case FILTER_CHANGE_KINDS.SET_TYPES: {
      return { ...filters, selectedTypes: change.filterKeys ? [...change.filterKeys] : [] };
    }

    case FILTER_CHANGE_KINDS.CLEAR_TYPES: {
      return { ...filters, selectedTypes: [] };
    }

    case FILTER_CHANGE_KINDS.SET_DATE_RANGE: {
      return {
        ...filters,
        startDate: change.startDate !== undefined ? change.startDate : filters.startDate,
        endDate: change.endDate !== undefined ? change.endDate : filters.endDate,
      };
    }

    case FILTER_CHANGE_KINDS.SET_FOCUSED_KEY: {
      return { ...filters, focusedKey: change.focusedKey || null };
    }

    case FILTER_CHANGE_KINDS.CLEAR_FOCUSED_KEY: {
      return { ...filters, focusedKey: null };
    }

    default:
      return filters;
  }
};

// Helper para compatibilidade: converte o contrato novo de volta para
// o formato esperado por APIs existentes (selectedTypes como array plano,
// sem o campo focusedKey).
export const toSelectedFilterTypes = (filters) => filters?.selectedTypes || [];

export const isFeatureFocused = (filters, filterKey) => {
  if (!filters?.focusedKey) return null; // sem foco ativo → todos OK
  return filters.focusedKey === filterKey;
};
